<template>
  <div class="portal-page">
    <header class="portal-header">
      <a class="portal-brand" href="/" aria-label="Qwips Service System">
        <img class="portal-logo" :src="logoSrc" alt="Qwips" />
      </a>

      <div class="language-switch" aria-label="Language">
        <button v-if="currentLocale !== 'en'" class="language-switch__button" type="button" @click="setLang('en')">
          ENG
        </button>
        <button v-if="currentLocale !== 'no' && currentLocale !== 'nb'" class="language-switch__button" type="button"
          @click="setLang('no')">
          NO
        </button>
      </div>
    </header>

    <main class="portal-main">
      <section class="intro-section" aria-labelledby="portal-title">
        <div class="intro-copy">
          <p class="intro-eyebrow">Qwips</p>
          <h1 id="portal-title">{{ $t("home.welcome") }}</h1>
        </div>
      </section>

      <section class="portal-grid" aria-label="Portals">
        <article v-for="item in availableNavigationProperties" :key="item.id" class="portal-card">
          <v-card class="portal-card__surface" :href="item.link || undefined" :to="item.route || undefined"
            elevation="0">
            <div class="portal-card__header">
              <span class="portal-card__icon-wrap">
                <v-icon size="42">{{ item.icon }}</v-icon>
              </span>
              <v-icon class="portal-card__arrow" size="22">mdi-arrow-right</v-icon>
            </div>

            <div class="portal-card__body">
              <h2>{{ item.name }}</h2>
              <p>{{ item.subtitle }}</p>
            </div>

            <ul class="feature-list">
              <li v-for="service in item.featureList" :key="service">
                <v-icon size="18">mdi-check-circle-outline</v-icon>
                <span>{{ service }}</span>
              </li>
            </ul>
            <div v-if="item.links && item.links.length" class="app-links">
              <a v-for="link in item.links" :key="link.name" :href="link.href" class="app-links__item"
                :aria-label="link.name">
                <img :src="link.image" :alt="link.name" />
              </a>
            </div>
          </v-card>
        </article>
      </section>

      <section class="portal-privacy-section" aria-label="Privacy">
        <a class="privacy-link" href="https://gettermscdn.com/view/uZL8t/privacy/en-us"
          target="_blank" rel="noopener noreferrer">
          <img :src="pdfIcon" alt="" height="18" width="18" />
          <span>
            <strong>{{ $t("home.privacy.rulename") }}</strong>
            <small>{{ $t("home.privacy.description") }}</small>
          </span>
          <v-icon size="18">mdi-open-in-new</v-icon>
        </a>
      </section>
    </main>
  </div>
</template>


<script>
import http from "@/api/client";
import { messages, normalizeLocale, setStoredLocale } from "@/plugins/i18n/config";
import pdfIcon from "@/assets/pdf.gif";
import appleGetImage from "@/assets/apple/get.svg";
import androidGetImage from "@/assets/android/get.png";

export default {
  name: "PortalSelectionPage",

  data() {
    return {
      options: null,
      navLinks: [],
      pdfIcon,
      logoSrc: "/api2/Report/logo",
    };
  },

  mounted() {
    this.checkInitialSetupStatus();
  },

  methods: {
    async checkInitialSetupStatus() {
      try {
        const response = await http().get("/api2/initialsetup");
        if (response?.status === 200) {
          await this.$router.replace({ name: "InitialSetupLanding" });
          return;
        }
      } catch (error) {
        // If the check fails we keep user on portal selection.
      }

      this.createLinks();
    },
    
    createLinks() {
      this.navLinks = [
        {
          enabled: false,
          id: "CustomerPortal",
          name: this.$t("home.customer.name"),
          route: "/customer",
          icon: "mdi-wechat",
          subtitle: this.$t("home.customer.description"),
          featureList: this.getFeatureList("home.customer.featurelist"),
          links: [],
        },
        {
          id: "ServiceProvider",
          enabled: true,
          name: this.$t("home.serviceprovider.name"),
          link: "/serviceprovider/",
          icon: "mdi-toolbox",
          subtitle: this.$t("home.serviceprovider.description"),
          featureList: this.getFeatureList("home.serviceprovider.featurelist"),
        },
        {
          id: "Technician",
          enabled: false,
          name: this.$t("home.technician.name"),
          link: "https://technician.qwips.net/deeplinking",
          icon: "mdi-cellphone-arrow-down",
          subtitle: this.$t("home.technician.description"),
          links: null,
          featureList: this.getFeatureList("home.technician.featurelist"),
        },
      ];

      http()
        .get("api2/app/portal")
        .then((response) => {
          this.options = response.data;
          this.setLink("CustomerPortal", this.options.CustomerPortal, this.options.CustomerPortalRedirect);
          this.setLink("Technician", this.options.Technician);

          if (this.options.Technician) {
            const portalLink = this.navLinks.find((x) => x.id === "Technician");
            portalLink.links = this.stringToAppLinks(this.options.TechnicianAppUrls);
          }

          if (this.options.CustomerApp) {
            const portalLink = this.navLinks.find((x) => x.id === "CustomerPortal");
            portalLink.links = this.stringToAppLinks(this.options.CustomerAppUrls);
          }
        });
    },

    parseFeatureList(features) {
      return features
        .split(/[;|]/)
        .map((feature) => feature.trim())
        .filter(Boolean);
    },

    getFeatureList(path) {
      return this.parseFeatureList(this.getRawMessage(path));
    },

    getRawMessage(path) {
      const locale = normalizeLocale(this.$i18n.locale);
      const localeMessages = messages[locale] || messages.en;
      const message = path.split(".").reduce((value, key) => value && value[key], localeMessages);
      return typeof message === "string" ? message : this.$t(path);
    },

    stringToAppLinks(links) {
      if (links && links.includes("|")) {
        const items = links.split("|");
        return [
          {
            name: "Apple",
            image: appleGetImage,
            href: items[0],
          },
          {
            name: "Google",
            image: androidGetImage,
            href: items[1],
          },
        ];
      }
      return null;
    },

    setLang(lang) {
      this.$i18n.locale = setStoredLocale(lang);
      this.createLinks();
    },

    setLink(name, enabled, newLink = null) {
      const navLink = this.navLinks.find((e) => e.id === name);
      if (navLink) {
        if (newLink != null && newLink !== "") {
          navLink.link = newLink;
          navLink.route = "";
        }
        navLink.enabled = enabled;
      }
    },
  },

  computed: {
    currentLocale() {
      return this.$i18n.locale;
    },

    availableNavigationProperties() {
      return this.navLinks.filter((e) => e.enabled);
    },
  },

  watch: {},
};
</script>

<style lang="scss" scoped>
.portal-page {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: #182434;
  background:
    linear-gradient(180deg, rgba(66, 122, 175, 0.06), rgba(255, 255, 255, 0) 260px),
    #f6f8fb;
}

.portal-header,
.portal-main,
.portal-footer {
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
}

.portal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 0 8px;
}

.portal-brand {
  display: inline-flex;
  align-items: center;
  min-width: 0;
}

.portal-logo {
  display: block;
  max-width: min(360px, 70vw);
  max-height: 96px;
  object-fit: contain;
  object-position: left center;
}

.language-switch {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
}

.language-switch__button {
  border: 1px solid rgba(66, 122, 175, 0.24);
  border-radius: 999px;
  background: #ffffff;
  color: #275b8c;
  cursor: pointer;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  padding: 11px 14px;
  transition: background-color 140ms ease, border-color 140ms ease, color 140ms ease;
}

.language-switch__button:hover,
.language-switch__button:focus-visible {
  background: #eaf3fb;
  border-color: rgba(66, 122, 175, 0.56);
  color: #173e64;
  outline: none;
}

.portal-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 6px 0 0px;
}

.intro-section {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 420px);
  gap: 18px;
  align-items: center;
}

.intro-copy {
  min-width: 0;
}

.intro-eyebrow {
  margin: 0 0 4px;
  color: #ec6906;
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.intro-section h1 {
  max-width: 760px;
  margin: 0;
  color: #142436;
  font-size: clamp(1.35rem, 2.6vw, 2.2rem);
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.15;
}

.privacy-link {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  border: 1px solid rgba(24, 36, 52, 0.1);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.95);
  color: inherit;
  text-decoration: none;
  box-shadow: none;
  opacity: 0.86;
}

.privacy-link strong,
.privacy-link small {
  display: block;
}

.privacy-link strong {
  color: #142436;
  font-size: 0.82rem;
  font-weight: 700;
}

.privacy-link small {
  display: -webkit-box;
  margin-top: 2px;
  overflow: hidden;
  color: #667789;
  font-size: 0.74rem;
  line-height: 1.3;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
}

.privacy-link:hover,
.privacy-link:focus-visible {
  border-color: rgba(66, 122, 175, 0.45);
  outline: none;
  opacity: 1;
}

.portal-privacy-section {
  margin-top: 4px;
}

.portal-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 14px;
}

.portal-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 14px;
}

.portal-card__surface {
  height: 100%;
  min-height: 330px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px 16px 14px;
  border: 1px solid rgba(24, 36, 52, 0.11);
  border-radius: 10px;
  background: #ffffff;
  color: #182434;
  text-decoration: none;
  transition: border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease, background-color 140ms ease;
}

.portal-card__surface:hover,
.portal-card__surface:focus-visible {
  border-color: rgba(66, 122, 175, 0.44);
  box-shadow: 0 10px 22px rgba(36, 65, 95, 0.1);
  outline: none;
  transform: translateY(-1px);
  background: #fcfdff;
}

.portal-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.portal-card__icon-wrap {
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eaf3fb;
  color: #427aaf;
}

.portal-card__arrow {
  color: #ec6906;
  opacity: 0.8;
}

.portal-card__body h2 {
  margin: 0;
  color: #142436;
  font-size: 1.06rem;
  font-weight: 800;
  letter-spacing: 0;
  line-height: 1.2;
}

.portal-card__body p {
  margin: 8px 0 0;
  color: #566779;
  font-size: 0.92rem;
  line-height: 1.45;
}

.feature-list {
  display: grid;
  gap: 7px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.feature-list li {
  display: grid;
  grid-template-columns: 17px minmax(0, 1fr);
  gap: 8px;
  align-items: start;
  color: #27394b;
  font-size: 0.88rem;
  line-height: 1.3;
}

.feature-list .v-icon {
  color: #44af42;
  margin-top: 1px;
}

.app-links {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: auto;
  padding-top: 8px;
}

.app-links__item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 8px;
}

.app-links__item:hover,
.app-links__item:focus-visible {
  background: rgba(66, 122, 175, 0.1);
  outline: none;
}

.app-links img {
  display: block;
  width: auto;
  max-width: 132px;
  max-height: 44px;
  object-fit: contain;
}

.portal-footer {
  padding: 0 0 18px;
  color: #6c7c8d;
  font-size: 0.8rem;
}

@media (max-width: 780px) {

  .portal-header,
  .portal-main,
  .portal-footer {
    width: min(100% - 24px, 1180px);
  }

  .portal-header {
    padding-top: 18px;
  }

  .intro-section {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .intro-section h1 {
    font-size: 1.6rem;
    line-height: 1.1;
  }

  .portal-grid {
    grid-template-columns: 1fr;
  }

  .portal-card__surface {
    min-height: 0;
    gap: 12px;
  }
}

@media (max-width: 520px) {
  .portal-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .language-switch {
    width: 100%;
    justify-content: flex-end;
  }

  .privacy-link {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .privacy-link .v-icon {
    display: none;
  }

  .portal-card__surface {
    padding: 20px;
  }
}
</style>
